import { useEffect, useState } from 'react';
import { AffilmasterApiError, getAffilmasterFinanceDashboard, type FinanceDashboard } from '../api/affilmaster';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { useFeather } from '../shell/useFeather';
import { fmtDen } from './affilmaster-format';
import './affilmaster-finance.css';

const SYMBOL: Record<string, string> = { CZK: 'Kč', EUR: '€' };
const MESICE = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];

function castka(value: number, currency: string, digits = 2): string {
  return `${value.toLocaleString('cs-CZ', { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${SYMBOL[currency] ?? currency}`;
}

function mesicLabel(ym: string): string {
  const month = Number(ym.slice(5, 7));
  return `${MESICE[month - 1] ?? ym} ${ym.slice(0, 4)}`;
}

function faktur(count: number): string {
  if (count === 1) return '1 faktura';
  if (count >= 2 && count <= 4) return `${count} faktury`;
  return `${count} faktur`;
}

function uctu(count: number): string {
  if (count === 1) return '1 účet';
  if (count >= 2 && count <= 4) return `${count} účty`;
  return `${count} účtů`;
}

function datum(value: string | null): string {
  if (!value) return 'datum zatím není dostupné';
  return fmtDen(value.slice(0, 10));
}

export function AffilmasterFinanceDashboardPage() {
  const [data, setData] = useState<FinanceDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAffilmasterFinanceDashboard()
      .then((next) => {
        if (!cancelled) {
          setData(next);
          setErr(null);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) setErr(reason instanceof AffilmasterApiError ? reason.message : 'Načtení přehledu selhalo');
      });
    return () => { cancelled = true; };
  }, []);

  useFeather([data]);

  return (
    <section className="am-fin">
      <header className="prototype-page-head am-fin-page-head">
        <div>
          <div className="prototype-eyebrow">AFFILMASTER</div>
          <h1>Finance</h1>
          <p>Aktuální peníze, otevřené pohledávky a výhled příjmů</p>
        </div>
        {data && (
          <div className="am-fin-head-meta" aria-label="Kontext finančních dat">
            <span><i data-feather="refresh-cw" />Účty k {datum(data.balances.asOf)}</span>
            <span>1 € = {castka(data.balances.rate, 'CZK', 3)}</span>
          </div>
        )}
      </header>

      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Finance se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}
      {!err && !data && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}
      {data && <FinanceContent data={data} />}
    </section>
  );
}

function FinanceContent({ data }: { data: FinanceDashboard }) {
  const balances = data.balances.totalCzk;
  const receivables = data.unpaidInvoices.totalCzk;
  const position = balances + receivables;
  const invoiceCount = data.unpaidInvoices.byCurrency.reduce((sum, row) => sum + row.count, 0);
  const overdueCount = data.unpaidInvoices.byCurrency.reduce((sum, row) => sum + row.overdueCount, 0);
  const accountsWithData = data.balances.byCurrency.reduce((sum, row) => sum + row.accounts, 0);
  const settlement = data.settlement;

  return (
    <div className="am-fin-content p1-arc-scope">
      <section className="am-fin-arc-grid" aria-label="Souhrn financí">
        <MetricCard
          label="Finanční pozice"
          value={position}
          suffix=" Kč"
          context="Peníze na účtech a otevřené pohledávky"
        />
        <MetricCard
          label="Na účtech"
          value={balances}
          suffix=" Kč"
          context={`${uctu(accountsWithData)} · stav k ${datum(data.balances.asOf)}`}
        />
        <MetricCard
          label="K úhradě"
          value={receivables}
          suffix=" Kč"
          context={`${faktur(invoiceCount)}${overdueCount > 0 ? ` · ${overdueCount} po splatnosti` : ''}`}
          change={overdueCount > 0 ? `${overdueCount} po splatnosti` : 'Bez prodlení'}
        />
        <MetricCard
          label="Očekávané příjmy"
          value={settlement.income.totalCzk}
          suffix=" Kč"
          context={`${mesicLabel(settlement.ym)} · bez DPH`}
        />
      </section>

      <section className="am-fin-arc-details" aria-label="Detail finančního přehledu">
        <article className="prototype-panel am-fin-arc-panel">
          <header>
            <div><span>BANKA</span><h2>Zůstatky na účtech</h2></div>
            <a href="./banka">Otevřít banku</a>
          </header>
          <div className="am-fin-arc-rows">
            {data.balances.byCurrency.map((row) => (
              <FinanceRow
                key={row.currency}
                label={`${row.currency} · ${uctu(row.accounts)}`}
                value={castka(row.amount, row.currency)}
                note={`Stav k ${datum(data.balances.asOf)}`}
              />
            ))}
          </div>
        </article>

        <article className="prototype-panel am-fin-arc-panel">
          <header>
            <div><span>POHLEDÁVKY</span><h2>Nezaplacené faktury</h2></div>
          </header>
          <div className="am-fin-arc-rows">
            {data.unpaidInvoices.byCurrency.map((row) => (
              <FinanceRow
                key={row.currency}
                label={`${row.currency} · ${faktur(row.count)}`}
                value={castka(row.amount, row.currency)}
                note={row.overdueCount > 0 ? `${row.overdueCount} po splatnosti · ${castka(row.overdueAmount, row.currency)}` : 'Bez faktur po splatnosti'}
                warning={row.overdueCount > 0}
              />
            ))}
          </div>
        </article>

        <article className="prototype-panel am-fin-arc-panel am-fin-arc-panel-wide">
          <header>
            <div><span>VÝHLED · {mesicLabel(settlement.ym).toUpperCase()}</span><h2>Skladba očekávaných příjmů</h2></div>
            <a href="./prijmy">Otevřít příjmy</a>
          </header>
          <div className="am-fin-arc-income">
            <FinanceRow label="Fixní fakturace" value={castka(settlement.fixed.totalCzk, 'CZK')} note="Aktivní fixní dealy" />
            <FinanceRow label="Provize AM" value={castka(settlement.fees.totalCzk, 'CZK')} note="Průběžný odhad z PaP" />
            <FinanceRow label="Celkem bez DPH" value={castka(settlement.income.totalCzk, 'CZK')} note={`${mesicLabel(settlement.ym)} · aktuální výhled`} strong />
          </div>
        </article>
      </section>
    </div>
  );
}

function FinanceRow({ label, value, note, warning = false, strong = false }: {
  label: string;
  value: string;
  note: string;
  warning?: boolean;
  strong?: boolean;
}) {
  return (
    <div className={`am-fin-arc-row${warning ? ' is-warning' : ''}${strong ? ' is-strong' : ''}`}>
      <div><span>{label}</span><small>{note}</small></div>
      <strong>{value}</strong>
    </div>
  );
}
